// The Suvaani header, shared by both courses (Prakrit pages through AppHeader, /sanskrit through the Sanskrit
// layout): the product mark, the course's sections in English (from xl; below that they live in the bottom bar),
// search, the preferences button (Kids / Adults and everything else live in the Preferences sheet), the Premium
// button and the account menu. Nothing in it wraps.

import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { Course } from '@/lib/plans'

import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import {
    Crown,
    LayoutGrid,
    LogIn,
    LogOut,
    Search,
    ShieldCheck,
    TrendingUp,
    User
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { canEdit, useSession } from '@/lib/client/session'
import { COURSE_HOME } from '@/lib/plans'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

export interface SiteNavItem {
    href: string
    label: string
    icon: LucideIcon
    /** Path prefixes that also mark this item as current. */
    match?: string[]
    /** Extra classes (the Prakrit Kids theme colours each item). */
    className?: string
    activeClassName?: string
}

export const COURSE_NAME: Record<Course, string> = { prakrit: 'Prakrit', sanskrit: 'Sanskrit' }

export const COURSE_LOGO: Record<Course | 'suvaani', string> = {
    suvaani: '/images/brand/suvaani.png',
    prakrit: '/images/brand/prakrit-suvaani.png',
    sanskrit: '/images/brand/sanskrit-suvaani.png'
}

/** Whether `href` is the current section. A course home matches only itself. */
export const isCurrent = (pathname: string, item: Pick<SiteNavItem, 'href' | 'match'>): boolean => {
    if (item.href === '/prakrit' || item.href === '/sanskrit' || item.href === '/') return pathname === item.href

    return (item.match ?? [item.href]).some(m => pathname === m || pathname.startsWith(`${m}/`))
}

/** The product mark: the Suvaani emblem and the name set in type ("Suvaani" over "PRAKRIT" or "SANSKRIT"). */
export const SuvaaniMark: FC<{ course?: Course, href?: string, className?: string }> = ({ course, href, className }): ReactNode => (
    <Link
        href={href ?? (course ? COURSE_HOME[course] : '/')}
        aria-label={course ? `${COURSE_NAME[course]} Suvaani, home` : 'Suvaani, home'}
        className={cn('flex shrink-0 items-center gap-2.5 rounded-xl', className)}
    >
        <Image
            src={COURSE_LOGO[course ?? 'suvaani']}
            alt=''
            width={44}
            height={44}
            priority
            className='kid-logo size-11 shrink-0 rounded-xl bg-white object-cover ring-1 ring-border'
        />
        <span className='flex flex-col leading-none'>
            <span className='font-heading text-[1.375rem] text-suvaani-deep'>Suvaani</span>
            {course && <span className='mt-1 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground'>{COURSE_NAME[course]}</span>}
        </span>
    </Link>
)

/** "Go Premium" for this course, nothing for staff or a member of this course. */
export const PremiumButton: FC<{ course: Course }> = ({ course }): ReactNode => {
    const { access, loading } = useSession()
    const [now, setNow] = useState<number | null>(null)

    useEffect(() => setNow(Date.now()), [])

    if (loading || now === null || access.source === 'staff') return null

    const member = access[course]

    // There is no free trial: a member of this course sees nothing here, everyone else "Go Premium".
    if (member) return null

    return (
        <Link
            href={`/pricing?plan=${course}`}
            className='group inline-flex h-10 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-linear-to-r from-saffron to-suvaani-sun px-3 text-sm font-bold text-ink shadow-md shadow-saffron/25 transition-[filter,transform] hover:-translate-y-px hover:brightness-105 sm:px-4'
        >
            <Crown className='size-4 transition-transform group-hover:-rotate-12' aria-hidden='true' />
            <span className='hidden sm:inline'>Go Premium</span>
            <span className='sr-only sm:hidden'>Go Premium</span>
        </Link>
    )
}

const AccountMenu: FC<{ course: Course }> = ({ course }): ReactNode => {
    const { user, loading, signOut } = useSession()
    const router = useRouter()

    if (loading) return <span className='size-10' aria-hidden='true' />

    if (!user) {
        return (
            <Link
                href={`/account?next=${encodeURIComponent(router.asPath)}`}
                className='inline-flex h-10 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-border bg-card px-3 text-sm font-semibold text-foreground transition-colors hover:border-saffron/60 hover:bg-muted sm:px-4'
            >
                <LogIn className='size-4' aria-hidden='true' />
                <span className='hidden sm:inline'>Sign in</span>
                <span className='sr-only sm:hidden'>Sign in</span>
            </Link>
        )
    }

    const progressHref = course === 'sanskrit' ? '/sanskrit/progress' : '/progress'

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                aria-label={`Account: ${user.displayName}`}
                className='inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-ink text-sm font-semibold text-paper ring-2 ring-transparent transition-shadow hover:ring-saffron/60'
            >
                {user.displayName.slice(0, 1).toUpperCase() || <User className='size-4' />}
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-60'>
                <DropdownMenuGroup>
                    <DropdownMenuLabel className='flex flex-col'>
                        <span className='font-semibold text-foreground'>{user.displayName}</span>
                        <span className='truncate text-xs font-normal text-muted-foreground'>{user.email}</span>
                    </DropdownMenuLabel>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => router.push(progressHref)}>
                    <TrendingUp className='size-4' /> My progress
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push('/explore')}>
                    <LayoutGrid className='size-4' /> All courses
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push(`/account?next=${encodeURIComponent(router.asPath)}`)}>
                    <User className='size-4' /> Account and settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => router.push('/pricing')}>
                    <Crown className='size-4' /> My Premium
                </DropdownMenuItem>
                {canEdit(user) && (
                    <DropdownMenuItem onClick={() => router.push('/admin')}>
                        <ShieldCheck className='size-4' /> Admin
                    </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onClick={async () => {
                        await signOut()
                        router.push('/')
                    }}
                >
                    <LogOut className='size-4' /> Sign out
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

interface SiteHeaderProps {
    course: Course
    nav: SiteNavItem[]
    onSearch: () => void
    /** The preferences button (both courses open the same Preferences sheet). */
    settings: ReactNode
    className?: string
}

/** Kids mode only (applied before paint through html[data-mode]): each section gets its own colour, in order. */
const KIDS_TONES = [
    { idle: 'in-data-[mode=kids]:hover:bg-kid-orange/15', active: 'in-data-[mode=kids]:bg-kid-orange/20 in-data-[mode=kids]:ring-2 in-data-[mode=kids]:ring-kid-orange' },
    { idle: 'in-data-[mode=kids]:hover:bg-kid-sky/15', active: 'in-data-[mode=kids]:bg-kid-sky/20 in-data-[mode=kids]:ring-2 in-data-[mode=kids]:ring-kid-sky' },
    { idle: 'in-data-[mode=kids]:hover:bg-kid-grass/15', active: 'in-data-[mode=kids]:bg-kid-grass/20 in-data-[mode=kids]:ring-2 in-data-[mode=kids]:ring-kid-grass' },
    { idle: 'in-data-[mode=kids]:hover:bg-kid-berry/15', active: 'in-data-[mode=kids]:bg-kid-berry/18 in-data-[mode=kids]:ring-2 in-data-[mode=kids]:ring-kid-berry' },
    { idle: 'in-data-[mode=kids]:hover:bg-kid-grape/15', active: 'in-data-[mode=kids]:bg-kid-grape/18 in-data-[mode=kids]:ring-2 in-data-[mode=kids]:ring-kid-grape' },
    { idle: 'in-data-[mode=kids]:hover:bg-kid-sun/30', active: 'in-data-[mode=kids]:bg-kid-sun/35 in-data-[mode=kids]:ring-2 in-data-[mode=kids]:ring-kid-sun' }
]

export const SiteHeader: FC<SiteHeaderProps> = ({ course, nav, onSearch, settings, className }): ReactNode => {
    const router = useRouter()

    return (
        <header className={cn('sticky top-0 z-40 border-b border-border/80 bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/75 in-data-[mode=kids]:border-transparent', className)}>
            {/* Kids mode: a scalloped rainbow edge hangs under the header (decorative). */}
            <span aria-hidden='true' className='kids-only kid-bunting pointer-events-none absolute inset-x-0 top-full' />
            <div className='mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6'>
                <SuvaaniMark course={course} />

                <nav aria-label='Primary' className='hidden min-w-0 flex-1 justify-center xl:flex'>
                    <ul className='flex items-center gap-1'>
                        {nav.map((item, index) => {
                            const active = isCurrent(router.pathname, item)
                            const Icon = item.icon
                            const tone = KIDS_TONES[index % KIDS_TONES.length]

                            return (
                                <li key={item.href}>
                                    <Link
                                        href={item.href}
                                        aria-current={active ? 'page' : undefined}
                                        className={cn(
                                            'inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full px-3 text-[0.9375rem] font-medium transition-colors 2xl:px-3.5',
                                            'in-data-[mode=kids]:h-11 in-data-[mode=kids]:font-semibold',
                                            active ? 'bg-saffron-soft font-semibold text-ink' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                            active ? tone.active : tone.idle,
                                            item.className,
                                            active && item.activeClassName
                                        )}
                                    >
                                        <Icon className={cn('hidden size-4 shrink-0 2xl:block', active ? 'text-saffron-strong' : 'opacity-70')} aria-hidden='true' />
                                        {item.label}
                                    </Link>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='ml-auto flex shrink-0 items-center gap-2 xl:ml-0'>
                    <button
                        type='button'
                        onClick={onSearch}
                        aria-label='Search (Ctrl K)'
                        title='Search (Ctrl K)'
                        className='inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:border-saffron/50 hover:text-foreground'
                    >
                        <Search className='size-4' aria-hidden='true' />
                    </button>
                    {settings}
                    <PremiumButton course={course} />
                    <AccountMenu course={course} />
                </div>
            </div>
        </header>
    )
}
